import type { FolderContentItem } from './folder-contents-page.model';
import {
  isVirtualChatsItem,
  type FolderContentsListItem,
} from './folder-contents-list-items';
import { getFolderContentItemId } from './folder-contents-page.model';

/** Per-row tree position, consumed by the list for indent + ARIA. */
export type LibraryTreeMeta = {
  /** 0 for top-level rows. Drives indentation and `aria-level` (which is 1-based). */
  depth: number;
  /** Folder with contents — gets a disclosure control. */
  hasChildren: boolean;
  isExpanded: boolean;
  /** Children are being fetched, so the control shows a pending state. */
  isLoadingChildren: boolean;
};

export type FlattenedLibraryTree = {
  /** Rows in render order, parents immediately followed by their children. */
  items: FolderContentsListItem[];
  /** Tree position by item id. */
  metaById: Map<string, LibraryTreeMeta>;
};

/**
 * Guard against a cycle introduced by a bad move (a folder reachable from
 * itself). Folder nesting is shallow in practice; this only exists so a corrupt
 * parent chain can't produce an unbounded row list.
 */
const MAX_TREE_DEPTH = 16;

/**
 * Whether a row is a folder that reports having contents.
 *
 * Chats count here even though they are not listed individually: an expanded
 * folder discloses them as its collapsed "Chats" row, so a folder holding
 * nothing else still has something to open. `chatCount` is already narrowed to
 * the chats that produce that row, so a folder of nothing but workflow runs
 * reports no children and keeps the arrow it would have opened onto an empty
 * list.
 */
function folderHasChildren(item: FolderContentsListItem): boolean {
  if (isVirtualChatsItem(item) || item.type !== 'folder') {
    return false;
  }
  return (item.data.childrenCount ?? 0) + (item.data.chatCount ?? 0) > 0;
}

/**
 * Splices expanded folders' children into the row list in place.
 *
 * This is what makes the top-level `All` view honest. The Library root returns
 * root folders plus *unfiled* artifacts, so anything filed into a folder simply
 * wasn't present — users who filed something and came back to `All` saw it
 * missing. Expanding in place makes filed items reachable without leaving the
 * view, and without a server change.
 *
 * Pure so the ordering and cycle behaviour can be tested without React.
 */
export function flattenLibraryTree(params: {
  rootItems: FolderContentsListItem[];
  expandedIds: ReadonlySet<string>;
  /**
   * List rows per expanded folder — the folder's own nested rows, not the raw
   * fetch: an expanded folder's children include its collapsed "Chats" row, so
   * these are `FolderContentsListItem`s rather than plain fetched items.
   */
  childrenByFolderId: ReadonlyMap<string, FolderContentsListItem[]>;
  loadingFolderIds: ReadonlySet<string>;
}): FlattenedLibraryTree {
  const { rootItems, expandedIds, childrenByFolderId, loadingFolderIds } =
    params;
  const items: FolderContentsListItem[] = [];
  const metaById = new Map<string, LibraryTreeMeta>();

  const walk = (
    levelItems: FolderContentsListItem[],
    depth: number,
    ancestors: ReadonlySet<string>
  ): void => {
    for (const item of levelItems) {
      const id = isVirtualChatsItem(item)
        ? item.data.id
        : getFolderContentItemId(item);
      const hasChildren = folderHasChildren(item);
      const isExpanded = hasChildren && expandedIds.has(id);

      items.push(item);
      metaById.set(id, {
        depth,
        hasChildren,
        isExpanded,
        isLoadingChildren: isExpanded && loadingFolderIds.has(id),
      });

      if (!isExpanded || depth + 1 >= MAX_TREE_DEPTH || ancestors.has(id)) {
        continue;
      }
      const children = childrenByFolderId.get(id);
      if (children === undefined || children.length === 0) {
        continue;
      }
      walk(children, depth + 1, new Set([...ancestors, id]));
    }
  };

  walk(rootItems, 0, new Set());
  return { items, metaById };
}

/**
 * How many of each expanded folder's children were left unfetched.
 *
 * Inline expansion pulls a single page per folder, so a folder holding more than
 * that would otherwise render as though fully expanded with the remainder
 * silently absent — and the folder row's own "N items" summary made the gap look
 * like a contradiction. The count here is the difference between the server's
 * total for the *same* query and what came back, so it stays correct under a type
 * filter or an active search (where the folder's raw `childrenCount` would not).
 *
 * Only folders that are expanded and have loaded get an entry: one still fetching
 * has no honest number yet, and a zero difference means nothing is hidden.
 *
 * Pure so the arithmetic can be tested without React.
 */
export function hiddenChildCounts(params: {
  expandedIds: ReadonlySet<string>;
  childrenByFolderId: ReadonlyMap<string, FolderContentItem[]>;
  childTotalsByFolderId: ReadonlyMap<string, number>;
}): ReadonlyMap<string, number> {
  const { expandedIds, childrenByFolderId, childTotalsByFolderId } = params;
  const hidden = new Map<string, number>();
  for (const folderId of expandedIds) {
    const loaded = childrenByFolderId.get(folderId);
    const total = childTotalsByFolderId.get(folderId);
    if (loaded === undefined || total === undefined) {
      continue;
    }
    const missing = total - loaded.length;
    if (missing > 0) {
      hidden.set(folderId, missing);
    }
  }
  return hidden;
}

/** Toggles a folder's expansion, returning a new set (never mutates). */
export function toggleExpanded(
  expandedIds: ReadonlySet<string>,
  folderId: string
): ReadonlySet<string> {
  const next = new Set(expandedIds);
  if (next.has(folderId)) {
    next.delete(folderId);
  } else {
    next.add(folderId);
  }
  return next;
}
